import React, { useEffect } from 'react'
import { Image, ImageStyle, TouchableOpacity, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useNavigation } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Button, Icon, Text } from '@components/base'
import { InlineAlert, ScrollViewWithFade } from '@src/components'
import { mfpIntegrationDarkImage, mfpIntegrationLightImage } from '@src/assets/images'
import { UiInlineAlert } from '@src/types'
import { useIsDarkMode } from '@src/config/theme'
import { tutorialGroupFromTutorialsSelector } from '@src/screens/Tutorials/models/tutorials.selectors'
import { TutorialGroups } from '@src/screens/Tutorials/models/tutorials.types'
import { Analytics, Bugsnag, CustomEventTypes, Device } from '@src/config'
import { useGoBack } from '@src/utils'
import { userSelector } from '@src/selectors/app'

export const MfpInstructionsScreen = () => {
  const styles = useStyleSheet(themedStyles)
  const navigation = useNavigation()
  const cmsTutorialGroup = useSelector(
    tutorialGroupFromTutorialsSelector(TutorialGroups.MyFitnessPal),
  )
  const dispatch = useDispatch()
  const goBack = useGoBack()
  const user = useSelector(userSelector)

  const tutorialId = cmsTutorialGroup?.tutorials.find(
    (tutorial) => tutorial.platform === Device.platform,
  )?.id

  const onFollowInstructionsPress = () => {
    if (tutorialId) {
      Analytics.track(CustomEventTypes.MfpInstructionsFollowInstructionsTapped, {
        userId: user?.id,
      })
      navigation.navigate('CMSTutorial', {
        tutorialId,
      })
    } else {
      Bugsnag.notify(new Error('MyFitnessPal tutorial ID not found'))
    }
  }

  useEffect(() => {
    dispatch({
      type: 'tutorials/fetchTutorials',
    })
  }, [dispatch])

  useEffect(() => {
    Analytics.track(CustomEventTypes.MfpInstructionsIntroScreenVisited, {
      userId: user?.id,
    })
  }, [user?.id])

  const isDarkMode = useIsDarkMode()
  const imageSource = isDarkMode
    ? mfpIntegrationDarkImage.imageSource
    : mfpIntegrationLightImage.imageSource

  return (
    <SafeAreaView edges={['bottom', 'top']} style={styles.container}>
      <ScrollViewWithFade style={styles.scrollView} fadeColor={styles.container.backgroundColor}>
        <TouchableOpacity onPress={goBack} accessibilityLabel="Back">
          <Icon name="x" size={20} />
        </TouchableOpacity>
        <View style={styles.imageContainer}>
          <Image source={imageSource} style={styles.image as ImageStyle} />
        </View>
        <View style={styles.textContent}>
          <Text type="title-2" style={styles.title}>
            Integrate with MyFitnessPal
          </Text>
          <Text type="large" style={styles.description}>
            Connect Nutrisense with MyFitnessPal to seamlessly import your meals and make logging
            faster and easier—no double entry needed.
          </Text>
        </View>
      </ScrollViewWithFade>
      <InlineAlert
        category={UiInlineAlert.Info}
        style={styles.instructionsAlert}
        message="Follow the steps to link your accounts and start syncing meals."
      />
      <Button
        type="primary"
        size="block"
        onPress={onFollowInstructionsPress}
        accessibilityLabel="Follow Instructions"
      >
        Follow Instructions
      </Button>
    </SafeAreaView>
  )
}

const themedStyles = StyleService.create({
  container: {
    flex: 1,
    paddingHorizontal: 16,
    paddingBottom: 16,
    backgroundColor: 'theme.background',
  },
  scrollView: {
    flex: 1,
    paddingTop: 8,
  },
  imageContainer: {
    flexGrow: 3,
    justifyContent: 'center',
  },
  image: {
    maxWidth: 250,
    height: undefined,
    maxHeight: 130,
    alignSelf: 'center',
    aspectRatio: 16 / 9,
  },
  textContent: {
    flexGrow: 1,
    paddingTop: 16,
  },
  title: {
    textAlign: 'center',
  },
  description: {
    paddingTop: 24,
    paddingBottom: 8,
    textAlign: 'center',
  },
  instructionsAlert: {
    marginBottom: 16,
  },
})
